import { useEffect } from "react"
import { useTernaryDarkMode } from "usehooks-ts"
import { useAuthStore } from "@/store/auth"

// Crisp types
declare global {
  interface Window {
    $crisp: any[]
    CRISP_WEBSITE_ID: string
  }
}

const CrispChat = () => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" })
  const { user, loggedIn } = useAuthStore()

  useEffect(() => {
    // Initialize Crisp
    window.$crisp = []
    window.CRISP_WEBSITE_ID = "2a11040a-791b-4af1-8216-296c40d36fdb"

    // Load Crisp script
    const script = document.createElement("script")
    script.src = "https://client.crisp.chat/l.js"
    script.async = true
    document.getElementsByTagName("head")[0].appendChild(script)

    // Configure Crisp after it loads
    script.onload = () => {
      // Set theme based on dark mode
      window.$crisp.push(["set", "theme", isDarkMode ? "dark" : "default"])
      
      // Configure widget position and appearance
      window.$crisp.push(["set", "position", "right"])
      window.$crisp.push(["set", "availability", "online"])
      
      // Set user data if logged in
      if (loggedIn && user) {
        window.$crisp.push(["set", "user:email", user.email])
        window.$crisp.push(["set", "user:nickname", `${user.firstName} ${user.lastName}`])
        window.$crisp.push(["set", "user:company", user.companyName || ""])
        
        // Set custom data for context
        window.$crisp.push(["set", "session:data", {
          user_tier: user.productTier || "free-trial",
          user_id: user.id,
          signup_date: user.createdAt,
          platform: "datacord"
        }])
      }

      // Configure chatbox colors for branding
      window.$crisp.push(["set", "theme:color", "#1677ff"])
      
      // Set up custom events for integration tracking
      setupIntegrationTracking()
    }

    return () => {
      // Cleanup script on unmount
      const scriptElement = document.querySelector('script[src="https://client.crisp.chat/l.js"]')
      if (scriptElement) {
        scriptElement.remove()
      }
    }
  }, [])

  // Update theme when dark mode changes
  useEffect(() => {
    if (window.$crisp) {
      window.$crisp.push(["set", "theme", isDarkMode ? "dark" : "default"])
    }
  }, [isDarkMode])

  // Update user data when auth state changes
  useEffect(() => {
    if (window.$crisp && loggedIn && user) {
      window.$crisp.push(["set", "user:email", user.email])
      window.$crisp.push(["set", "user:nickname", `${user.firstName} ${user.lastName}`])
      window.$crisp.push(["set", "user:company", user.companyName || ""])
      
      window.$crisp.push(["set", "session:data", {
        user_tier: user.productTier || "free-trial",
        user_id: user.id,
        signup_date: user.createdAt,
        platform: "datacord"
      }])
    }
  }, [loggedIn, user])

  return null
}

// Helper function to set up integration flow tracking
const setupIntegrationTracking = () => {
  // Track page changes for integration flow
  const trackPageChange = () => {
    const path = window.location.pathname
    const searchParams = new URLSearchParams(window.location.search)
    
    let context = {
      page: path,
      timestamp: new Date().toISOString()
    }

    // Detect integration flow pages and add context
    if (path.includes('/source-configuration')) {
      context = {
        ...context,
        flow_step: 'source_configuration',
        integration_type: searchParams.get('type') || 'unknown'
      }
    } else if (path.includes('/data-mapping')) {
      context = {
        ...context,
        flow_step: 'data_mapping',
        integration_type: searchParams.get('type') || 'unknown'
      }
    } else if (path.includes('/integration-review')) {
      context = {
        ...context,
        flow_step: 'integration_review',
        integration_type: searchParams.get('type') || 'unknown'
      }
    } else if (path.includes('/dashboard')) {
      context = {
        ...context,
        flow_step: 'dashboard'
      }
    } else if (path.includes('/sign-up')) {
      context = {
        ...context,
        flow_step: 'signup'
      }
    }

    // Send context to Crisp
    if (window.$crisp) {
      window.$crisp.push(["set", "session:event", [{
        type: "page_view",
        data: context
      }]])
    }
  }

  // Track initial page load
  trackPageChange()

  // Track page changes (for SPA navigation)
  let currentPath = window.location.pathname
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname
      trackPageChange()
    }
  })
  
  observer.observe(document.body, { childList: true, subtree: true })
}

// Utility functions to call from other components
export const crispUtils = {
  // Track specific events
  trackEvent: (eventType: string, data: any) => {
    if (window.$crisp) {
      window.$crisp.push(["set", "session:event", [{
        type: eventType,
        data: {
          ...data,
          timestamp: new Date().toISOString()
        }
      }]])
    }
  },

  // Track errors
  trackError: (error: string, context?: any) => {
    if (window.$crisp) {
      window.$crisp.push(["set", "session:event", [{
        type: "error",
        data: {
          error_message: error,
          context,
          timestamp: new Date().toISOString(),
          page: window.location.pathname
        }
      }]])
    }
  },

  // Track integration progress
  trackIntegrationStep: (step: string, integrationData: any) => {
    if (window.$crisp) {
      window.$crisp.push(["set", "session:event", [{
        type: "integration_progress",
        data: {
          step,
          ...integrationData,
          timestamp: new Date().toISOString(),
          page: window.location.pathname
        }
      }]])
    }
  },

  // Show chat widget
  showChat: () => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:show"])
    }
  },

  // Hide chat widget
  hideChat: () => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:hide"])
    }
  },

  // Open chat with pre-filled message
  openChatWithMessage: (message: string) => {
    if (window.$crisp) {
      window.$crisp.push(["do", "message:send", ["text", message]])
      window.$crisp.push(["do", "chat:open"])
    }
  },

  // Set user status (for premium users)
  setUserSegment: (segment: string) => {
    if (window.$crisp) {
      window.$crisp.push(["set", "user:segments", [segment]])
    }
  }
}

export default CrispChat
